/* ==========================================================================
   IMPULSE CRO — theme runtime
   Vanilla JS only. No dependencies. Everything is progressive enhancement:
   forms, links and <details> accordions work with JS disabled.
   ========================================================================== */
(function () {
  'use strict';

  var Theme = window.ImpulseTheme || {};
  var routes = Theme.routes || {};
  var strings = Theme.strings || {};
  var money = Theme.moneyFormat || '${{amount}}';

  /* ----------------------------------------------------------- helpers -- */

  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }

  function on(el, evt, fn, opts) { if (el) el.addEventListener(evt, fn, opts); }

  /* Mirrors Shopify's `money` filter so JS-computed prices (bundle tiers,
     variant switches) match the server-rendered ones exactly. */
  function formatMoney(cents) {
    var placeholder = money.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = placeholder ? placeholder[1] : 'amount';
    var decimals = key.indexOf('no_decimals') > -1 ? 0 : 2;
    var thousands = ',';
    var decimal = '.';

    if (key.indexOf('period_and_space') > -1) { thousands = ' '; decimal = '.'; }
    else if (key.indexOf('comma_separator') > -1) { thousands = '.'; decimal = ','; }
    else if (key.indexOf('apostrophe_separator') > -1) { thousands = "'"; decimal = '.'; }
    else if (key.indexOf('space_separator') > -1) { thousands = ' '; decimal = ','; }

    var parts = (Math.round(cents) / 100).toFixed(decimals).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var str = decimals ? whole + decimal + parts[1] : whole;

    return money.replace(/\{\{\s*\w+\s*\}\}/, str);
  }
  Theme.formatMoney = formatMoney;

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function lockScroll(lock) {
    document.body.classList.toggle('is-locked', !!lock);
  }

  function trapFocus(container) {
    var focusables = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return function () {};
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
    return function () { container.removeEventListener('keydown', handler); };
  }

  /* --------------------------------------------------------- pub / sub -- */

  var bus = {
    on: function (name, fn) { document.addEventListener('impulse:' + name, fn); },
    emit: function (name, detail) {
      document.dispatchEvent(new CustomEvent('impulse:' + name, { detail: detail || {} }));
    }
  };
  Theme.bus = bus;

  /* ------------------------------------------------------- cart client -- */

  var Cart = {
    busy: false,

    /* Section ids are printed by each cart-aware section, because inside a
       JSON template a section's real id looks like "template--123__main". */
    sectionsToRender: function () {
      return $$('[data-cart-section]')
        .map(function (el) { return el.getAttribute('data-cart-section'); })
        .filter(function (id, i, all) { return id && all.indexOf(id) === i; })
        .join(',');
    },

    request: function (url, body) {
      Cart.busy = true;
      bus.emit('cart:loading', { loading: true });
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (res) {
          Cart.busy = false;
          bus.emit('cart:loading', { loading: false });
          if (!res.ok) throw new Error(res.data.description || res.data.message || strings.cartError || 'Cart error');
          if (res.data.sections) Cart.renderSections(res.data.sections);
          bus.emit('cart:updated', { data: res.data });
          return res.data;
        })
        .catch(function (err) {
          Cart.busy = false;
          bus.emit('cart:loading', { loading: false });
          bus.emit('cart:error', { message: err.message });
          throw err;
        });
    },

    renderSections: function (sections) {
      Object.keys(sections).forEach(function (id) {
        if (!sections[id]) return;
        var selector = '[data-cart-section="' + id + '"]';
        var target = document.querySelector(selector);
        if (!target) return;
        var parsed = new DOMParser().parseFromString(sections[id], 'text/html');
        var fresh = parsed.querySelector(selector);
        if (!fresh) return;
        /* Swap the inside only: the root keeps its classes, so an open drawer
           stays open and does not replay its slide-in transition. */
        target.innerHTML = fresh.innerHTML;
      });
      Cart.syncCount();
      bus.emit('sections:rendered', {});
    },

    syncCount: function () {
      var source = $('[data-cart-count-source]');
      if (!source) return;
      var count = parseInt(source.getAttribute('data-cart-count-source'), 10) || 0;
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    add: function (payload) {
      payload.sections = Cart.sectionsToRender();
      /* fall through: an empty list simply means nothing needs re-rendering */
      payload.sections_url = window.location.pathname;
      return Cart.request(routes.cartAdd || '/cart/add.js', payload);
    },

    change: function (line, quantity) {
      return Cart.request(routes.cartChange || '/cart/change.js', {
        line: line,
        quantity: quantity,
        sections: Cart.sectionsToRender(),
        sections_url: window.location.pathname
      });
    },

    updateAttributes: function (attributes) {
      return Cart.request(routes.cartUpdate || '/cart/update.js', {
        attributes: attributes,
        sections: Cart.sectionsToRender(),
        sections_url: window.location.pathname
      });
    }
  };
  Theme.Cart = Cart;

  /* ------------------------------------------------------- cart drawer -- */

  var Drawer = {
    releaseFocus: null,

    el: function () { return $('[data-cart-drawer]'); },

    open: function () {
      var drawer = Drawer.el();
      if (!drawer) { window.location.href = routes.cartUrl || '/cart'; return; }
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      Drawer.releaseFocus = trapFocus(drawer);
      ReserveTimer.start();
    },

    close: function () {
      var drawer = Drawer.el();
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (Drawer.releaseFocus) { Drawer.releaseFocus(); Drawer.releaseFocus = null; }
    }
  };
  Theme.Drawer = Drawer;

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-cart-open]');
    if (openBtn) {
      if (!Drawer.el()) return; // let the link go to /cart
      e.preventDefault();
      Drawer.open();
      return;
    }

    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      Drawer.close();
      return;
    }

    var remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      Cart.change(remove.getAttribute('data-line'), 0);
      return;
    }

    var step = e.target.closest('[data-qty-step]');
    if (step) {
      e.preventDefault();
      var wrap = step.closest('.qty');
      var input = $('input', wrap);
      if (!input) return;
      var min = parseInt(input.getAttribute('min'), 10) || 0;
      var next = (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-qty-step'), 10);
      if (next < min) next = min;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (e.target.closest('[data-menu-open]')) {
      var menu = $('[data-menu-drawer]');
      if (menu) { menu.classList.add('is-open'); lockScroll(true); trapFocus(menu); }
      return;
    }
    if (e.target.closest('[data-menu-close]')) {
      var m = $('[data-menu-drawer]');
      if (m) { m.classList.remove('is-open'); lockScroll(false); }
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    Cart.change(input.getAttribute('data-line'), qty);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = $('.drawer.is-open') || $('.menu-drawer.is-open');
    if (!open) return;
    if (open.classList.contains('menu-drawer')) { open.classList.remove('is-open'); lockScroll(false); }
    else Drawer.close();
  });

  bus.on('cart:loading', function (e) {
    var drawer = Drawer.el();
    if (drawer) drawer.classList.toggle('is-busy', !!e.detail.loading);
  });

  bus.on('cart:error', function (e) {
    var box = $('[data-cart-error]');
    if (box) { box.textContent = e.detail.message; box.hidden = false; }
    else window.alert(e.detail.message);
  });

  /* ----------------------------------------------------- reserve timer -- */

  var ReserveTimer = {
    key: 'impulse:reserve-start',
    tick: null,

    start: function () {
      var nodes = $$('[data-reserve-timer]');
      if (!nodes.length) return;
      var minutes = parseFloat(nodes[0].getAttribute('data-reserve-timer')) || 5;
      var total = minutes * 60 * 1000;
      var started = parseInt(sessionStorage.getItem(ReserveTimer.key), 10);

      if (!started || isNaN(started) || Date.now() - started > total) {
        started = Date.now();
        try { sessionStorage.setItem(ReserveTimer.key, String(started)); } catch (err) {}
      }

      clearInterval(ReserveTimer.tick);
      function render() {
        var left = Math.max(0, total - (Date.now() - started));
        var mm = Math.floor(left / 60000);
        var ss = Math.floor((left % 60000) / 1000);
        var text = mm + ':' + (ss < 10 ? '0' : '') + ss;
        $$('[data-reserve-output]').forEach(function (el) {
          el.textContent = text;
          el.setAttribute('datetime', 'PT' + mm + 'M' + ss + 'S');
        });
        if (left <= 0) {
          clearInterval(ReserveTimer.tick);
          $$('[data-reserve-timer]').forEach(function (el) {
            var expired = el.getAttribute('data-reserve-expired');
            if (expired) el.innerHTML = '<span>' + expired + '</span>';
          });
        }
      }
      render();
      ReserveTimer.tick = setInterval(render, 1000);
    },

    reset: function () {
      try { sessionStorage.removeItem(ReserveTimer.key); } catch (err) {}
    }
  };

  bus.on('sections:rendered', function () { ReserveTimer.start(); });

  /* ------------------------------------------------------ product form -- */

  function initProductForms(scope) {
    $$('[data-product-form]', scope || document).forEach(function (form) {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';

      on(form, 'submit', function (e) {
        if (!Theme.ajaxCart) return; // fall back to a normal POST
        e.preventDefault();
        var btn = form.querySelector('[data-add-button]');
        var errorBox = form.querySelector('[data-cart-error]');
        if (errorBox) errorBox.hidden = true;
        if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }

        var formData = new FormData(form);
        var payload = {
          items: [{
            id: Number(formData.get('id')),
            quantity: Number(formData.get('quantity') || 1),
            properties: {}
          }]
        };

        formData.forEach(function (value, key) {
          var match = key.match(/^properties\[(.+)\]$/);
          if (match && String(value).trim() !== '') payload.items[0].properties[match[1]] = value;
        });
        if (!Object.keys(payload.items[0].properties).length) delete payload.items[0].properties;

        Cart.add(payload)
          .then(function () {
            ReserveTimer.reset();
            Drawer.open();
          })
          .catch(function () {})
          .then(function () {
            if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
          });
      });
    });
  }

  /* --------------------------------------------------- variant picking -- */

  function initVariants(scope) {
    $$('[data-variant-root]', scope || document).forEach(function (root) {
      if (root.dataset.bound === 'true') return;
      root.dataset.bound = 'true';

      var dataEl = $('[data-variant-json]', root);
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      var form = $('[data-product-form]', root);
      var idInput = form ? form.querySelector('[name="id"]') : null;
      var updateUrl = root.getAttribute('data-update-url') === 'true';
      var firstRender = true;

      function selectedOptions() {
        return $$('[data-variant-option]:checked', root).map(function (i) { return i.value; });
      }

      function findVariant(options) {
        return variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === options[i]; });
        });
      }

      function render() {
        var opts = selectedOptions();
        var variant = findVariant(opts) || null;

        // Mark unavailable combinations on the last option group.
        $$('[data-variant-option]', root).forEach(function (input) {
          var label = root.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          var test = opts.slice();
          test[Number(input.getAttribute('data-option-index'))] = input.value;
          var match = findVariant(test);
          label.classList.toggle('is-unavailable', !match || !match.available);
        });

        opts.forEach(function (value, i) {
          var out = root.querySelector('[data-option-selected-' + i + ']');
          if (out) out.textContent = value;
        });

        if (idInput && variant) idInput.value = variant.id;

        var available = variant && variant.available;
        $$('[data-add-button]', root).forEach(function (btn) {
          btn.disabled = !available;
          var label = btn.querySelector('[data-add-label]');
          if (!label) return;
          if (!variant) label.textContent = strings.unavailable || 'Unavailable';
          else if (!variant.available) label.textContent = strings.soldOut || 'Sold out';
          else label.textContent = btn.getAttribute('data-default-label') || label.textContent;
        });

        if (variant) {
          root.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } }));
          Bundles.setBase(variant.price, variant.compare_at_price);
          if (!firstRender) {
            updatePriceDisplay(root, variant);
            scrollGalleryToVariant(root, variant);
          }
          if (!firstRender && updateUrl && window.history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url.toString());
          }
        }
      }

      $$('[data-variant-option]', root).forEach(function (input) { on(input, 'change', render); });
      render();
      firstRender = false;
    });
  }

  function updatePriceDisplay(root, variant) {
    var price = $('[data-price-current]', root);
    var compare = $('[data-price-compare]', root);
    var save = $('[data-price-save]', root);
    if (price) price.textContent = formatMoney(variant.price);
    var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (compare) {
      compare.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
      compare.hidden = !hasCompare;
    }
    if (save) {
      if (hasCompare) {
        var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        save.textContent = (strings.savePercent || 'Save {{ percent }}%').replace('{{ percent }}', pct);
        save.hidden = false;
      } else {
        save.hidden = true;
      }
    }
    $$('[data-sticky-price]').forEach(function (el) { el.textContent = formatMoney(variant.price); });
  }

  function scrollGalleryToVariant(root, variant) {
    if (!variant.featured_media) return;
    var slide = root.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
    var viewport = $('[data-gallery-viewport]', root);
    if (!slide || !viewport) return;
    viewport.scrollTo({ left: slide.offsetLeft - viewport.offsetLeft, behavior: 'smooth' });
  }

  /* -------------------------------------------------------- gallery ----- */

  function initGalleries(scope) {
    $$('[data-gallery]', scope || document).forEach(function (gallery) {
      if (gallery.dataset.bound === 'true') return;
      gallery.dataset.bound = 'true';

      var viewport = $('[data-gallery-viewport]', gallery);
      var slides = $$('[data-gallery-slide]', gallery);
      var thumbs = $$('[data-gallery-thumb]', gallery);
      var dots = $$('[data-gallery-dot]', gallery);
      if (!viewport || slides.length < 2) return;

      function setActive(index) {
        thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === index); });
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === index);
          d.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      thumbs.concat(dots).forEach(function (btn) {
        on(btn, 'click', function () {
          var i = Number(btn.getAttribute('data-index'));
          var slide = slides[i];
          if (slide) viewport.scrollTo({ left: slide.offsetLeft - viewport.offsetLeft, behavior: 'smooth' });
        });
      });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              setActive(slides.indexOf(entry.target));
            }
          });
        }, { root: viewport, threshold: [0.61] });
        slides.forEach(function (s) { io.observe(s); });
      } else {
        on(viewport, 'scroll', debounce(function () {
          setActive(Math.round(viewport.scrollLeft / viewport.clientWidth));
        }, 80));
      }

      setActive(0);
    });
  }

  /* --------------------------------------------------------- bundles ---- */

  var Bundles = {
    root: null,
    basePrice: 0,
    baseCompare: 0,

    init: function (scope) {
      $$('[data-bundles]', scope || document).forEach(function (root) {
        if (root.dataset.bound === 'true') return;
        root.dataset.bound = 'true';
        Bundles.root = root;
        Bundles.basePrice = Number(root.getAttribute('data-base-price')) || 0;
        Bundles.baseCompare = Number(root.getAttribute('data-base-compare')) || 0;

        $$('[data-bundle-input]', root).forEach(function (input) {
          on(input, 'change', function () { Bundles.apply(input); });
        });

        var checked = $('[data-bundle-input]:checked', root);
        if (checked) Bundles.apply(checked);
      });
    },

    apply: function (input) {
      var root = input.closest('[data-bundles]');
      $$('[data-bundle-option]', root).forEach(function (opt) {
        opt.classList.toggle('is-active', opt.contains(input));
      });

      var qty = Number(input.getAttribute('data-quantity')) || 1;
      var code = input.getAttribute('data-discount-code') || '';
      var label = input.getAttribute('data-bundle-label') || '';

      var form = document.querySelector('[data-product-form]');
      if (form) {
        var qtyInput = form.querySelector('[name="quantity"]');
        if (qtyInput) qtyInput.value = qty;
        var prop = form.querySelector('[data-bundle-property]');
        if (prop) prop.value = qty > 1 ? label : '';
      }

      $$('[data-checkout-link]').forEach(function (link) {
        link.setAttribute('data-discount-code', code);
      });
      try { sessionStorage.setItem('impulse:discount', code); } catch (e) {}

      Bundles.refreshTotals(root);
    },

    setBase: function (price, compare) {
      Bundles.basePrice = price;
      Bundles.baseCompare = compare && compare > price ? compare : price;
      var root = $('[data-bundles]');
      if (root) {
        root.setAttribute('data-base-price', price);
        Bundles.refreshTotals(root);
      }
    },

    refreshTotals: function (root) {
      if (!root) return;
      $$('[data-bundle-option]', root).forEach(function (opt) {
        var input = $('[data-bundle-input]', opt);
        if (!input) return;
        var qty = Number(input.getAttribute('data-quantity')) || 1;
        var discount = Number(input.getAttribute('data-discount')) || 0;
        var total = Math.round(Bundles.basePrice * qty * (1 - discount / 100));
        var was = Math.round(Math.max(Bundles.baseCompare, Bundles.basePrice) * qty);

        var totalEl = $('[data-bundle-total]', opt);
        var wasEl = $('[data-bundle-was]', opt);
        var eachEl = $('[data-bundle-each]', opt);
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (wasEl) {
          wasEl.textContent = formatMoney(was);
          wasEl.hidden = was <= total;
        }
        if (eachEl) {
          eachEl.textContent = (strings.perUnit || '{{ price }} each')
            .replace('{{ price }}', formatMoney(Math.round(total / qty)));
        }
      });
    }
  };
  Theme.Bundles = Bundles;

  /* Carry the chosen tier's discount code through to checkout. */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-checkout-link]');
    if (!link) return;
    var code = link.getAttribute('data-discount-code');
    if (!code) { try { code = sessionStorage.getItem('impulse:discount') || ''; } catch (err) {} }
    if (!code) return;
    if (link.tagName === 'A') {
      e.preventDefault();
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/checkout';
    } else {
      var form = link.closest('form');
      if (!form || form.querySelector('[name="discount"]')) return;
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'discount';
      hidden.value = code;
      form.appendChild(hidden);
    }
  });

  /* ------------------------------------------------------ sticky ATC ---- */

  function initStickyAtc() {
    var bar = $('[data-sticky-atc]');
    var anchor = $('[data-atc-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(anchor);

    on($('[data-sticky-submit]', bar), 'click', function (e) {
      e.preventDefault();
      var form = $('[data-product-form]');
      if (!form) return;
      var realBtn = form.querySelector('[data-add-button]');
      if (realBtn && !realBtn.disabled) realBtn.click();
    });
  }

  /* --------------------------------------------------------- reveals ---- */

  function initReveals() {
    var nodes = $$('.reveal');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* --------------------------------------------------------- marquee ---- */

  function initMarquees() {
    $$('[data-marquee]', document).forEach(function (marquee) {
      var track = $('.marquee__track', marquee);
      var group = $('.marquee__group', track);
      if (!track || !group || track.children.length > 1) return;
      track.appendChild(group.cloneNode(true)); // seamless loop
      var speed = Number(marquee.getAttribute('data-speed')) || 26;
      marquee.style.setProperty('--marquee-speed', speed + 's');
    });
  }

  /* ------------------------------------------------------------ init ---- */

  function initAll(scope) {
    initProductForms(scope);
    initVariants(scope);
    initGalleries(scope);
    Bundles.init(scope);
    initMarquees();
    Cart.syncCount();
  }

  function boot() {
    initAll(document);
    initStickyAtc();
    initReveals();
    ReserveTimer.start();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); initStickyAtc(); });
  bus.on('sections:rendered', function () { initAll(document); });

  window.ImpulseTheme = Theme;
})();
